// ---------------------------------------------------------------------------
// ConfirmDialog.js – Reusable confirmation modal for irreversible actions
// ---------------------------------------------------------------------------
// Usage:
//   <ConfirmDialog
//     open={showConfirm}
//     title="Confirm Sale"
//     message="Sell 10,000 shares of FIEE at $5.25?"
//     detail="Lot #42 · Total proceeds: $52,500.00"
//     confirmLabel="Execute Sale"
//     variant="danger"            // "danger" | "warning" | "primary"
//     onConfirm={() => { ... }}
//     onCancel={() => setShowConfirm(false)}
//   />
// ---------------------------------------------------------------------------
import React from "react";

const VARIANTS = {
  danger:  { bg: "rgba(239, 68, 68, 0.12)", border: "rgba(239, 68, 68, 0.35)", btn: "#ef4444", btnHover: "#dc2626", icon: "⚠" },
  warning: { bg: "rgba(245, 158, 11, 0.12)", border: "rgba(245, 158, 11, 0.35)", btn: "#f59e0b", btnHover: "#d97706", icon: "⚠" },
  primary: { bg: "rgba(59, 130, 246, 0.12)", border: "rgba(59, 130, 246, 0.35)", btn: "#3b82f6", btnHover: "#2563eb", icon: "ℹ" },
};

export default function ConfirmDialog({
  open,
  title = "Confirm Action",
  message,
  detail,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "danger",
  onConfirm,
  onCancel,
}) {
  if (!open) return null;

  const v = VARIANTS[variant] || VARIANTS.danger;

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 1100,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "rgba(0, 0, 0, 0.7)", backdropFilter: "blur(4px)",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div
        style={{
          background: "var(--fin-surface, #141820)",
          border: "1px solid var(--fin-border, #262d3a)",
          borderRadius: "10px",
          width: "90vw", maxWidth: "420px",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5)",
          animation: "slideUp 0.2s ease-out",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "18px 24px",
            borderBottom: "1px solid var(--fin-border, #262d3a)",
            display: "flex", alignItems: "center", gap: "10px",
          }}
        >
          <span style={{ fontSize: "18px" }}>{v.icon}</span>
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--fin-sans, -apple-system, sans-serif)",
              fontSize: "15px", fontWeight: 600,
              color: "var(--fin-text, #e2e8f0)",
              textTransform: "uppercase", letterSpacing: "0.02em",
            }}
          >
            {title}
          </h2>
        </div>

        {/* Body */}
        <div style={{ padding: "20px 24px" }}>
          {/* Warning banner */}
          <div
            style={{
              background: v.bg, border: `1px solid ${v.border}`,
              borderRadius: "6px", padding: "12px 14px", marginBottom: detail ? "12px" : "0",
            }}
          >
            <p
              style={{
                margin: 0,
                fontFamily: "var(--fin-sans, -apple-system, sans-serif)",
                fontSize: "14px", lineHeight: "1.5",
                color: "var(--fin-text, #e2e8f0)",
              }}
            >
              {message}
            </p>
          </div>

          {/* Optional detail line */}
          {detail && (
            <p
              style={{
                margin: 0,
                fontFamily: "var(--fin-mono, monospace)",
                fontSize: "12px",
                color: "var(--fin-text-dim, #7a8599)",
                padding: "0 2px",
              }}
            >
              {detail}
            </p>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "0 24px 20px",
            display: "flex", justifyContent: "flex-end", gap: "10px",
          }}
        >
          <button
            onClick={onCancel}
            style={{
              fontFamily: "var(--fin-sans, -apple-system, sans-serif)",
              fontSize: "13px", fontWeight: 600,
              padding: "9px 20px", borderRadius: "6px",
              border: "1px solid var(--fin-border, #262d3a)",
              background: "var(--fin-surface-2, #1a1f2b)",
              color: "var(--fin-text-dim, #7a8599)",
              cursor: "pointer",
            }}
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            style={{
              fontFamily: "var(--fin-sans, -apple-system, sans-serif)",
              fontSize: "13px", fontWeight: 600,
              padding: "9px 20px", borderRadius: "6px",
              border: "none",
              background: v.btn,
              color: "#fff",
              cursor: "pointer",
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
